import React, { useCallback, useMemo } from 'react';
import { Table } from 'antd';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import BorderlessButton from '../../../common/buttons/BorderlessButton';
import PencilIcon from '../../../common/icons/files/PencilIcon';
import TrashIcon from '../../../common/icons/files/TrashIcon';
import VerticallyCenteredNumberInput from '../../../common/inputs/VerticallyCenteredNumberInput';
import Colors from '../../../constants/Colors';
import { currencyFormatter, currencyParser } from '../../../helpers/inputParsers';

import { hasDates, totalCategoryValues } from '../../projectHelpers';
import BudgetTrackingCostCodesTable from './BudgetTrackingCostCodesTable';
import BudgetTrackingEmptyState from './BudgetTrackingEmptyState';
import { buildColumns, getColumnOptions } from './manage/costTrackingHelpers';

export const getPhaseColumns = (customCategories = []) => [
  { key: 'name', title: 'Phase', dataIndex: 'name' },
  { key: 'description', title: 'Description', dataIndex: 'description' },
  {
    key: 'estimatedLabourHours',
    title: 'Estimated Labour Hours',
    dataIndex: 'estimatedLabourHours',
    width: 150,
  },
  {
    key: 'estimatedLabourCost',
    title: 'Estimated Labour Cost',
    dataIndex: 'estimatedLabourCost',
    width: 150,
  },
  {
    key: 'estimatedOverheadHours',
    title: 'Estimated Overhead Hours',
    dataIndex: 'estimatedOverheadHours',
    width: 150,
  },
  {
    key: 'estimatedOverheadCost',
    title: 'Estimated Overhead Cost',
    dataIndex: 'estimatedOverheadCost',
    width: 150,
  },
  {
    key: 'estimatedMaterialCost',
    title: 'Estimated Material Cost',
    dataIndex: 'estimatedMaterialCost',
    width: 150,
  },
  {
    key: 'estimatedEquipmentHours',
    title: 'Estimated Equipment Hours',
    dataIndex: 'estimatedEquipmentHours',
    width: 150,
  },
  {
    key: 'estimatedEquipmentCost',
    title: 'Estimated Equipment Cost',
    dataIndex: 'estimatedEquipmentCost',
    width: 150,
  },
  ...customCategories.map((customCategory) => ({
    key: `phaseEstimate.${customCategory.id}`,
    title: `Estimated ${customCategory.name} Cost`,
    dataIndex: `phaseEstimate.${customCategory.id}`,
    width: 150,
  })),
  {
    key: 'actions',
    title: '',
    label: 'Actions',
    width: 100,
    align: 'center',
    showIf: ({ showActions }) => showActions,
  },
];

export const getPhaseColumnOptions = ({ customCategories, showActions } = {}) => (
  getColumnOptions(getPhaseColumns(customCategories), { showActions })
);

function PhaseExpandedContent({
  record,
  onEditPhaseCostCodes,
  useBuildEstimate,
  emptyText,
  dates,
  isNotDisplay,
  isExpanded,
  hiddenColumnKeys,
}) {
  const phase = { id: record.id, name: record.name, costcodes: record.costcodes ?? [] };

  if (phase.costcodes.length === 0) {
    return (
      <div style={{ padding: 16 }}>
        <BudgetTrackingEmptyState text={emptyText || 'No cost codes in this phase'} />
      </div>
    );
  }

  return (
    <div className="budget-tracking-phase-expanded-content">
      <BudgetTrackingCostCodesTable
        unphasedCostcodes={[]}
        phases={[phase]}
        expandedData={isExpanded ? phase.costcodes : undefined}
        isNotDisplay={isNotDisplay}
        onEditPhaseCostCodes={onEditPhaseCostCodes}
        showEditButton={!isNotDisplay && !!onEditPhaseCostCodes}
        useBuildEstimate={useBuildEstimate}
        emptyText={emptyText || 'No cost codes'}
        dates={dates}
        hiddenColumnKeys={hiddenColumnKeys}
      />
    </div>
  );
}

PhaseExpandedContent.propTypes = {
  record: PropTypes.shape({
    costcodes: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
    id: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  onEditPhaseCostCodes: PropTypes.func,
  useBuildEstimate: PropTypes.bool,
  emptyText: PropTypes.string,
  dates: PropTypes.bool,
  isNotDisplay: PropTypes.bool,
  isExpanded: PropTypes.bool,
  hiddenColumnKeys: PropTypes.instanceOf(Set),
};

PhaseExpandedContent.defaultProps = {
  onEditPhaseCostCodes: null,
  useBuildEstimate: false,
  emptyText: null,
  dates: false,
  isNotDisplay: false,
  isExpanded: false,
  hiddenColumnKeys: undefined,
};

export default function BudgetTrackingPhasesTable({
  isNotDisplay,
  dataSource,
  showEditButton,
  showDeleteButton,
  onEditPhaseDetails,
  openAddDrawer,
  onRemove,
  onEditPhaseCostCodes,
  useBuildEstimate,
  emptyText,
  useDates,
  isExpanded,
  hiddenColumnKeys,
  costcodeHiddenColumnKeys,
}) {
  const categories = useSelector((state) => state.costcodes.categories);

  const weHaveDates = useMemo(() => hasDates(dataSource || []), [dataSource]);
  const dates = useDates || weHaveDates;

  const customCategories = useMemo(() => (
    Object.values(categories || {}).filter((c) => !c?.isDefault)
  ), [categories]);

  const onEditPhaseEstimates = useCallback((id, key) => (newValue) => {
    if (!onEditPhaseDetails) return;
    let normalized = 0;
    if (typeof newValue === 'number' && !Number.isNaN(newValue)) {
      normalized = newValue;
    } else if (newValue != null && newValue !== '' && !Number.isNaN(Number(newValue))) {
      normalized = Number(newValue);
    }
    onEditPhaseDetails(id, { [key]: normalized });
  }, [onEditPhaseDetails]);

  const getNumberInput = useCallback((value, id, key, isCurrency = false) => (
    <VerticallyCenteredNumberInput
      min={0}
      value={value}
      style={{ width: 120 }}
      onChange={onEditPhaseEstimates(id, key)}
      asFloat
      formatter={isCurrency ? currencyFormatter : undefined}
      parser={isCurrency ? currencyParser : undefined}
    />
  ), [onEditPhaseEstimates]);

  const phaseColumns = useMemo(() => {
    const columnProps = {
      name: {
        render: (text, record) => record.name || text,
      },
      description: {
        render: (text) => text ?? '—',
      },
      estimatedLabourHours: {
        render: (text, record) => {
          if (isNotDisplay && showEditButton && onEditPhaseDetails) {
            return getNumberInput(text, record.id, 'estimatedLabourHours');
          }
          return text ?? '—';
        },
      },
      estimatedLabourCost: {
        render: (text, record) => {
          if (isNotDisplay && showEditButton && onEditPhaseDetails) {
            return getNumberInput(text, record.id, 'estimatedLabourCost', true);
          }
          return text ? currencyFormatter(text) : '—';
        },
      },
      estimatedOverheadHours: {
        render: (text, record) => {
          if (isNotDisplay && showEditButton && onEditPhaseDetails) {
            return getNumberInput(text, record.id, 'estimatedOverheadHours');
          }
          return text ?? '—';
        },
      },
      estimatedOverheadCost: {
        render: (text, record) => {
          if (isNotDisplay && showEditButton && onEditPhaseDetails) {
            return getNumberInput(text, record.id, 'estimatedOverheadCost', true);
          }
          return text ? currencyFormatter(text) : '—';
        },
      },
      estimatedMaterialCost: {
        render: (text, record) => {
          if (isNotDisplay && showEditButton && onEditPhaseDetails) {
            return getNumberInput(text, record.id, 'estimatedMaterialCost', true);
          }
          return text ? currencyFormatter(text) : '—';
        },
      },
      estimatedEquipmentHours: {
        render: (text, record) => {
          if (isNotDisplay && showEditButton && onEditPhaseDetails) {
            return getNumberInput(text, record.id, 'estimatedEquipmentHours');
          }
          return text ?? '—';
        },
      },
      estimatedEquipmentCost: {
        render: (text, record) => {
          if (isNotDisplay && showEditButton && onEditPhaseDetails) {
            return getNumberInput(text, record.id, 'estimatedEquipmentCost', true);
          }
          return text ? currencyFormatter(text) : '—';
        },
      },
      actions: {
        render: (_, record, index) => (
          <span style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
            {showEditButton && (
              <BorderlessButton
                title=""
                iconNode={<PencilIcon width={14} height={14} color={Colors.GRAY_950} />}
                onClick={() => {
                  if (openAddDrawer) {
                    openAddDrawer(index + 1); // 0 is new phase
                  }
                }}
              />
            )}
            {showDeleteButton && (
              <BorderlessButton
                title=""
                iconNode={<TrashIcon color={Colors.RED_600} width={14} height={14} />}
                onClick={() => onRemove?.(record)}
              />
            )}
          </span>
        ),
      },
    };
    customCategories.forEach((customCategory) => {
      const key = `phaseEstimate.${customCategory.id}`;
      columnProps[key] = {
        render: (text, record) => {
          if (isNotDisplay && showEditButton && onEditPhaseDetails) {
            return getNumberInput(text, record.id, key, true);
          }
          return text ? currencyFormatter(text) : '—';
        },
      };
    });

    return buildColumns({
      columnDefinitions: getPhaseColumns(customCategories),
      flags: { showActions: showEditButton || showDeleteButton },
      columnProps,
      hiddenKeySet: hiddenColumnKeys,
    });
  }, [
    isNotDisplay,
    showEditButton,
    showDeleteButton,
    onEditPhaseDetails,
    openAddDrawer,
    onRemove,
    customCategories,
    getNumberInput,
    hiddenColumnKeys,
  ]);

  const expandedRowRender = useCallback((record) => (
    <PhaseExpandedContent
      record={record}
      onEditPhaseCostCodes={onEditPhaseCostCodes}
      useBuildEstimate={useBuildEstimate}
      emptyText={emptyText}
      dates={dates}
      isNotDisplay={isNotDisplay}
      isExpanded={isExpanded}
      hiddenColumnKeys={costcodeHiddenColumnKeys}
    />
  ), [
    onEditPhaseCostCodes,
    useBuildEstimate,
    emptyText,
    dates,
    isNotDisplay,
    isExpanded,
    costcodeHiddenColumnKeys,
  ]);

  const expandable = useMemo(() => ({
    expandedRowRender,
    rowExpandable: (record) => (record.costcodes?.length ?? 0) > 0,
  }), [expandedRowRender]);

  const tableData = useMemo(() => {
    const phases = dataSource || [];
    return phases.map((item) => {
      const cc = item.costcodes;
      const cat = categories;
      const baseData = {
        ...item,
        key: item.id,
        estimatedLabourHours: item.estimatedLabourHours ?? totalCategoryValues(cc, cat, 'Labor', 'hours'),
        estimatedLabourCost: item.estimatedLabourCost ?? totalCategoryValues(cc, cat, 'Labor', 'estimatedCost'),
        estimatedOverheadHours: item.estimatedOverheadHours ?? totalCategoryValues(cc, cat, 'Overhead', 'hours'),
        estimatedOverheadCost: item.estimatedOverheadCost ?? totalCategoryValues(cc, cat, 'Overhead', 'estimatedCost'),
        estimatedMaterialCost: item.estimatedMaterialCost ?? totalCategoryValues(cc, cat, 'Material', 'estimatedCost'),
        estimatedEquipmentCost: item.estimatedEquipmentCost ?? totalCategoryValues(cc, cat, 'Equipment', 'estimatedCost', true),
        estimatedEquipmentHours: item.estimatedEquipmentHours ?? totalCategoryValues(cc, cat, 'Equipment', 'hours'),
      };
      const customCategoryData = {};
      customCategories.forEach((customCategory) => {
        const key = `phaseEstimate.${customCategory.id}`;
        customCategoryData[key] = item[key] ?? totalCategoryValues(cc, cat, customCategory.name, 'estimatedCost', true);
      });
      return { ...baseData, ...customCategoryData };
    });
  }, [dataSource, categories, customCategories]);

  return (
    <Table
      className="budget-tracking-table"
      size="small"
      columns={phaseColumns}
      dataSource={tableData}
      pagination={false}
      rowKey="key"
      expandable={expandable}
      scroll={{ x: 'max-content' }}
      locale={{
        emptyText: <BudgetTrackingEmptyState text="No phases" />,
      }}
      onHeaderRow={() => ({ className: 'ontraccr-table-header' })}
    />
  );
}

const costcodeShape = PropTypes.shape({
  id: PropTypes.string,
  categoryAssignments: PropTypes.shape({}),
});
BudgetTrackingPhasesTable.propTypes = {
  isNotDisplay: PropTypes.bool,
  dataSource: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    description: PropTypes.string,
    costcodes: PropTypes.arrayOf(costcodeShape),
    estimatedLabourHours: PropTypes.number,
    estimatedLabourCost: PropTypes.number,
    estimatedOverheadHours: PropTypes.number,
    estimatedOverheadCost: PropTypes.number,
    estimatedMaterialCost: PropTypes.number,
    estimatedEquipmentHours: PropTypes.number,
    estimatedEquipmentCost: PropTypes.number,
  })),
  showEditButton: PropTypes.bool,
  showDeleteButton: PropTypes.bool,
  onEditPhaseDetails: PropTypes.func,
  openAddDrawer: PropTypes.func,
  onRemove: PropTypes.func,
  onEditPhaseCostCodes: PropTypes.func,
  useBuildEstimate: PropTypes.bool,
  emptyText: PropTypes.string,
  useDates: PropTypes.bool,
  isExpanded: PropTypes.bool,
  hiddenColumnKeys: PropTypes.instanceOf(Set),
  costcodeHiddenColumnKeys: PropTypes.instanceOf(Set),
};

BudgetTrackingPhasesTable.defaultProps = {
  isNotDisplay: false,
  dataSource: [],
  showEditButton: false,
  showDeleteButton: false,
  onEditPhaseDetails: null,
  openAddDrawer: null,
  onRemove: null,
  onEditPhaseCostCodes: null,
  useBuildEstimate: false,
  emptyText: null,
  useDates: false,
  isExpanded: false,
  hiddenColumnKeys: undefined,
  costcodeHiddenColumnKeys: undefined,
};
